import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import Input from '../UI/Input'
import LoadingSpinner from '../UI/LoadingSpinner'
import { X, User, Mail, Shield } from 'lucide-react'
import sasUsersAPI from '../../services/sasUsersAPI'
import { rolesAPI } from '../../services/rolesAPI'
import toast from 'react-hot-toast'

export default function EditSASUserModal({ isOpen, onClose, user, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  // Fetch hub roles (control_center) for SaaS users
  const { data: rolesData, isLoading: rolesLoading } = useQuery(
    ['hub-roles'],
    () => rolesAPI.getAll({ user_type: 'control_center' }),
    { enabled: isOpen }
  )
  const roles = rolesData?.data?.data?.roles || []

  // Reset form when user data changes
  useEffect(() => {
    if (isOpen && user) {
      reset({
        full_name: user.full_name || '',
        email: user.email || '',
        status: user.status || 'active',
        role_id: user.role_id ? String(user.role_id) : ''
      })
    }
  }, [isOpen, user, reset])

  const updateUserMutation = useMutation(
    (userData) => sasUsersAPI.update(user.id, userData),
    {
      onSuccess: () => {
        toast.success('User updated successfully!')
        queryClient.invalidateQueries(['sas-users'])
        queryClient.invalidateQueries(['sas-users-all'])
        onSuccess && onSuccess()
        handleClose()
      },
      onError: (error) => {
        console.error('Error updating user:', error)
        toast.error(error.response?.data?.message || 'Failed to update user')
      }
    }
  )

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      await updateUserMutation.mutateAsync({
        ...data,
        role_id: data.role_id ? String(data.role_id) : null
      })
    } catch (error) {
      // Error handled in mutation
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset()
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <User className="h-4 w-4 text-blue-600" />
            </div>
            <CardTitle>Edit User</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* User Info */}
            <div className="space-y-4">
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <Input
                  id="full_name"
                  type="text"
                  {...register('full_name', { 
                    required: 'Full name is required',
                    minLength: {
                      value: 2,
                      message: 'Full name must be at least 2 characters'
                    }
                  })}
                  className="w-full"
                  placeholder="Enter full name"
                  error={errors.full_name?.message}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className="w-full"
                  placeholder="Enter email address"
                  error={errors.email?.message}
                  disabled={isSubmitting}
                  readOnly
                />
              </div>

              <div>
                <label htmlFor="role_id" className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                {rolesLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : (
                  <select
                    id="role_id"
                    {...register('role_id')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                    disabled={isSubmitting}
                  >
                    <option value="">No role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={String(role.id)}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                )}
                {errors.role_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.role_id.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  id="status"
                  {...register('status', { required: 'Status is required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-gray-100 cursor-not-allowed"
                  disabled
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                )}
              </div>
            </div>

            {/* User Info Display */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-900">User Information</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Created:</strong> {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</p>
                <p><strong>Last Login:</strong> {user?.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="min-w-[120px]"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}


